import { Eyebrow, LinkButton, Pill } from '@/components/ui';
import { BarbellIcon, DumbbellIcon, FlameIcon, SparkIcon, TargetIcon, TimerIcon, WatchIcon, GridIcon } from '@/lib/icons';

/**
 * Six tracks, six specialist AI coaches. Lift2Fit is not a lifting app: the same
 * log serves strength work, bodybuilding, conditioning, athletic performance,
 * wellness and general fitness — and the AI coach that reads it is the
 * specialist for whichever one you are training.
 */
export const TRACKS = [
  {
    id: 'strength',
    name: 'Strength',
    line: 'Get genuinely stronger.',
    body: 'Squat, bench, deadlift and press progressions built on top-set load and estimated 1RM, with the overload earned rather than assumed.',
    tracks: ['e1RM', 'top-set load', 'tonnage'],
    coach: 'AI Strength Coach',
    icon: <BarbellIcon className="text-lg" />,
  },
  {
    id: 'hypertrophy',
    name: 'Bodybuilding',
    line: 'Build muscle, deliberately.',
    body: 'Weekly sets per muscle group held inside evidence-based landmarks, weak points prioritised, supersets and tempo work where they earn their place.',
    tracks: ['sets per muscle', 'volume', 'bodyweight'],
    coach: 'AI Hypertrophy Coach',
    icon: <DumbbellIcon className="text-lg" />,
  },
  {
    id: 'conditioning',
    name: 'Conditioning',
    line: 'Engine work that fits your week.',
    body: 'Indoor engine work — erg intervals, sleds, carries and circuits — prescribed as work:rest with real intensity targets, measured in interval minutes and erg metres rather than tonnage.',
    tracks: ['interval minutes', 'work:rest', 'erg metres'],
    coach: 'AI Conditioning Coach',
    icon: <TimerIcon className="text-lg" />,
  },
  {
    id: 'performance',
    name: 'Performance',
    line: 'Train for power and explosiveness.',
    body: 'In-gym athletic work: jumps, medicine-ball throws, plyometrics and sled accelerations, programmed with energy-system support and readiness tracked session to session.',
    tracks: ['power markers', 'jump height', 'readiness'],
    coach: 'AI Performance Coach',
    icon: <TargetIcon className="text-lg" />,
  },
  {
    id: 'wellness',
    name: 'Wellness & recovery',
    line: 'Move well, feel better, stay consistent.',
    body: 'Mobility, stability and low-impact work with a pain-free-range rule, habit-led consistency, and no PR-chasing language anywhere near it.',
    tracks: ['mobility sessions', 'pain score', 'consistency'],
    coach: 'AI Wellness Coach',
    icon: <FlameIcon className="text-lg" />,
  },
  {
    id: 'general',
    name: 'General fitness',
    line: 'Everything, sensibly balanced.',
    body: 'A blend of resistance and indoor conditioning for people who want to be fit, capable and consistent without specialising — the safe default.',
    tracks: ['volume', 'sessions per week', 'consistency'],
    coach: 'AI Fitness Coach',
    icon: <SparkIcon className="text-lg" />,
  },
] as const;

export function Tracks() {
  return (
    <section id="tracks" className="border-b border-hairline bg-page-subtle py-20">
      <div className="mx-auto max-w-[1200px] 2xl:max-w-[1440px] min-[1920px]:max-w-[1720px] px-4 sm:px-6">
        <div className="mb-10 flex flex-col gap-4">
          <Eyebrow>
            <GridIcon className="mr-1 inline text-sm" /> One log · six specialist AI coaches
          </Eyebrow>
          <h2 className="font-display text-h2 font-black uppercase leading-tight tracking-[-0.01em] text-ink md:text-display-2">
            An AI coach for your specific need.
          </h2>
          <p className="max-w-[78ch] text-body-lg text-ink-muted">
            Lift2Fit is not a lifting app. It is one training log for strengthening, bodybuilding, indoor
            conditioning, gym-based athletic performance, wellness and all-round fitness — and the AI coach
            that reads it is the specialist for the track you are on. Every programme is built to be completed
            in a normal commercial gym. The tracker is free and genuinely good on its own; the AI coach is the
            upgrade.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {TRACKS.map((track) => (
            <article
              key={track.id}
              className="flex flex-col gap-3 rounded-card border border-hairline bg-surface p-5 shadow-card"
            >
              <div className="flex items-center justify-between">
                <span className="inline-flex h-10 w-10 items-center justify-center bg-brand-charcoal text-brand-white">
                  {track.icon}
                </span>
                <Pill tone="accent">{track.coach}</Pill>
              </div>
              <h3 className="font-display text-[17px] font-black uppercase tracking-[-0.01em] text-ink">
                {track.name}
              </h3>
              <p className="text-[13px] font-bold text-accent">{track.line}</p>
              <p className="text-[13px] leading-relaxed text-ink-muted">{track.body}</p>
              <div className="mt-auto flex flex-wrap gap-1.5 border-t border-hairline pt-3">
                <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Tracks</span>
                {track.tracks.map((metric) => (
                  <span
                    key={metric}
                    className="l2f-tabular rounded-pill border border-hairline px-2 py-[2px] font-mono text-[10px] text-ink"
                  >
                    {metric}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-card border border-hairline bg-surface px-5 py-4">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
            {[
              { icon: <WatchIcon className="text-accent" />, label: 'Web · iOS · Android · watchOS' },
              { icon: <GridIcon className="text-accent" />, label: 'The Grid replaces the spreadsheet' },
              { icon: <TimerIcon className="text-accent" />, label: 'Gym-based · every session indoors' },
              { icon: <SparkIcon className="text-accent" />, label: 'Coaches get a roster' },
            ].map((item) => (
              <span key={item.label} className="flex items-center gap-2 text-[12px] font-bold text-ink">
                {item.icon}
                {item.label}
              </span>
            ))}
          </div>
          <LinkButton href="#pricing" size="md">
            Choose your track
          </LinkButton>
        </div>
      </div>
    </section>
  );
}
